import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api } from './api';
import type { ChildSession, Role } from './types';

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'child'; child: ChildSession }
  | { status: 'parent' };

type AuthContextValue = {
  state: AuthState;
  refresh: () => Promise<void>;
  loginChild: (child_id: number, pin: string) => Promise<ChildSession>;
  loginParent: (pin: string) => Promise<void>;
  logout: () => Promise<void>;
  role: Role | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      if (me.role === 'child') setState({ status: 'child', child: me.child });
      else setState({ status: 'parent' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ status: 'anonymous' });
      } else {
        setState({ status: 'anonymous' });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loginChild = useCallback(async (child_id: number, pin: string) => {
    const res = await api.loginChild(child_id, pin);
    setState({ status: 'child', child: res.child });
    return res.child;
  }, []);

  const loginParent = useCallback(async (pin: string) => {
    await api.loginParent(pin);
    setState({ status: 'parent' });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ status: 'anonymous' });
    }
  }, []);

  const role: Role | null =
    state.status === 'child' ? 'child' : state.status === 'parent' ? 'parent' : null;

  return (
    <AuthContext.Provider value={{ state, refresh, loginChild, loginParent, logout, role }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth used outside AuthProvider');
  return ctx;
}
